import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import AuthPage from './pages/AuthPage';

// Lazy-loaded pages for code splitting
const HomePage           = lazy(() => import('./pages/HomePage'));
const EditorPage         = lazy(() => import('./pages/EditorPage'));
const CompressPdfPage    = lazy(() => import('./pages/seo/CompressPdfPage'));
const CompressPdf100kbPage = lazy(() => import('./pages/seo/CompressPdf100kbPage'));
const MergePdfPage       = lazy(() => import('./pages/seo/MergePdfPage'));
const PdfToWordPage      = lazy(() => import('./pages/seo/PdfToWordPage'));
const PdfToExcelPage     = lazy(() => import('./pages/seo/PdfToExcelPage'));
const AiSummarizerPage   = lazy(() => import('./pages/seo/AiSummarizerPage'));
const BlogPage           = lazy(() => import('./pages/blog/BlogPage'));
const BlogPost           = lazy(() => import('./pages/blog/BlogPost'));
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage  = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage     = lazy(() => import('./pages/TermsOfUsePage'));
const ContactPage        = lazy(() => import('./pages/ContactPage'));

function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#f8fafc,#eff6ff)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Sora',sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 64, height: 64,
          background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
          borderRadius: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, margin: '0 auto 20px',
          boxShadow: '0 8px 32px rgba(59,130,246,0.3)',
        }}>
          📄
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
          FreePDFMaster.in
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</div>
        <div style={{
          width: 48, height: 3,
          background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
          borderRadius: 2, margin: '20px auto 0',
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      </div>
    </div>
  );
}

// Requires the user to be signed in; otherwise redirects to /auth
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

// Redirects authenticated users away from /auth → back to /
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth */}
        <Route path="/auth" element={
          <GuestRoute><AuthPage /></GuestRoute>
        } />

        {/* Protected app */}
        <Route path="/" element={
          <ProtectedRoute><HomePage /></ProtectedRoute>
        } />
        <Route path="/editor" element={
          <ProtectedRoute><EditorPage /></ProtectedRoute>
        } />

        {/* Public SEO tool pages */}
        <Route path="/compress-pdf"       element={<CompressPdfPage />} />
        <Route path="/compress-pdf-100kb" element={<CompressPdf100kbPage />} />
        <Route path="/merge-pdf"          element={<MergePdfPage />} />
        <Route path="/pdf-to-word"        element={<PdfToWordPage />} />
        <Route path="/pdf-to-excel"       element={<PdfToExcelPage />} />
        <Route path="/ai-summarizer"      element={<AiSummarizerPage />} />

        {/* Blog */}
        <Route path="/blog"         element={<BlogPage />} />
        <Route path="/blog/:slug"   element={<BlogPost />} />

        {/* Legal & contact */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use"   element={<TermsOfUsePage />} />
        <Route path="/contact"        element={<ContactPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
